import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PropertyUnitView } from '../../enums/property.enums';
import { PropertyUnitModel } from '../../pages/Property/ManageCreateProperty/models/property_unit.model';
import { ManageCreatePropertySteps } from '../../pages/Property/ManageCreateProperty/components/ManageCreatePropertyProgressBar';
import PropertyFileTypeModel from '../../pages/Property/ManageCreateProperty/models/property_file_type.model';
import PropertyDetailsModel from '../../pages/Murabaha/models/property_details.model';
import { PropertyImageType } from '../../pages/Property/ManageCreateProperty/types/property.type';

interface PropertyUnitsState {
  separateOwnersUnits: PropertyUnitModel[];
  currentUnitDetailsStep: PropertyUnitView;
  currentTabStep: ManageCreatePropertySteps;
  errors: string[];
  editPropertyUnit?: PropertyUnitModel;
  // Docs and images
  legalDocsFiles: PropertyFileTypeModel[];
  imagesFiles: PropertyImageType[];
  //
  fractionalOwnersUnits: PropertyUnitModel[];
  //
  property: PropertyDetailsModel;
  editProperty?: PropertyDetailsModel;
}

const initialState: PropertyUnitsState = {
  separateOwnersUnits: [],
  currentUnitDetailsStep: PropertyUnitView.manualEntry,
  currentTabStep: ManageCreatePropertySteps.BASIC_DETAILS,
  errors: [],
  //
  legalDocsFiles: [],
  imagesFiles: [],
  //
  fractionalOwnersUnits: [],
  //
  property: new PropertyDetailsModel({}),
};

const propertyUnitsSlice = createSlice({
  name: 'propertyUnits',
  initialState,
  reducers: {
    setPropertyUnitsToState: (state, action: PayloadAction<PropertyUnitModel[]>) => {
      state.separateOwnersUnits = action.payload;
    },

    setPropertyUnitsFromExcelReportToState: (
      state,
      action: PayloadAction<PropertyUnitModel[]>
    ) => {
      const units = action.payload.filter(
        (n) =>
          !state.separateOwnersUnits.some((u) => u.unit_number === n.unit_number)
      );

      state.separateOwnersUnits = [...units, ...state.separateOwnersUnits];
    },

    setCurrentStepToUnitState: (state, action: PayloadAction<PropertyUnitView>) => {
      state.currentUnitDetailsStep = action.payload;
    },

    addPropertyUnitToState: (state, action: PayloadAction<PropertyUnitModel>) => {
      const index = state.separateOwnersUnits.findIndex(
        (unit) => unit.id === action.payload.id
      );

      // Case 1: If unit with the same number exists, update it (edit flow)
      if (index !== -1) {
        state.separateOwnersUnits[index] = action.payload;
        state.editPropertyUnit = undefined; // Clear edit state after saving
      }
      // Case 2: If it's a new unit, add it to the list
      else {
        state.separateOwnersUnits.push(action.payload.copyWith({ id: Date.now() })); // Assign a temporary ID (in real case, backend would provide this)
      }

      // Change the current step to the entries view after adding/editing a unit
      state.currentUnitDetailsStep = PropertyUnitView.manualEntry;
    },

    setCurrentTabStepToUnitState: (
      state,
      action: PayloadAction<ManageCreatePropertySteps>
    ) => {
      state.currentTabStep = action.payload;
    },

    setErrorsToUnitState: (state, action: PayloadAction<string[]>) => {
      state.errors = action.payload;
    },

    removeUnitEntryFromState: (state, action: PayloadAction<PropertyUnitModel>) => {
      // Remove from separate owners units
      state.separateOwnersUnits = state.separateOwnersUnits.filter(
        (unit) => unit.id !== action.payload.id
      );
      // Remove from fractional owners units (in case it's a fractional unit)
      state.fractionalOwnersUnits = state.fractionalOwnersUnits.filter(
        (unit) => unit.id !== action.payload.id
      );
      state.editPropertyUnit = undefined; // Clear edit state if the deleted unit was being edited
      //
      if (state.separateOwnersUnits.length === 0) {
        state.currentUnitDetailsStep = PropertyUnitView.uploadExcel; // Reset to initial step if no units left
      }
    },

    setEditPropertyUnitToState: (
      state,
      action: PayloadAction<PropertyUnitModel | undefined>
    ) => {
      state.editPropertyUnit = action.payload;
      if (action.payload) {
        state.currentUnitDetailsStep = PropertyUnitView.addUnitEntry;
      }
    },

    // Docs and images as list of files
    setBulkLegalDocsFilesToUnitState: (
      state,
      action: PayloadAction<PropertyFileTypeModel[]>
    ) => {
      state.legalDocsFiles = action.payload;
    },

    // Docs and images reducers
    setLegalDocsFilesToUnitState: (
      state,
      action: PayloadAction<PropertyFileTypeModel>
    ) => {
      const index = state.legalDocsFiles.findIndex(
        (file) => file.id === action.payload.id
      );
      if (index !== -1) {
        state.legalDocsFiles[index] = action.payload;
      } else {
        state.legalDocsFiles.push(action.payload);
      }
    },

    removeLegalDocsFileFromUnitState: (
      state,
      action: PayloadAction<PropertyFileTypeModel>
    ) => {
      state.legalDocsFiles = state.legalDocsFiles.filter(
        (file) => file.id !== action.payload.id
      );
    },

    setImagesFilesToUnitState: (
      state,
      action: PayloadAction<PropertyImageType[]>
    ) => {
      const images = [...state.imagesFiles, ...action.payload];
      // Remove duplicate files based on name
      state.imagesFiles = images.filter(
        (file, index, self) => index === self.findIndex((f) => f.id === file.id)
      );
    },

    removeImageFileFromUnitState: (
      state,
      action: PayloadAction<PropertyImageType>
    ) => {
      state.imagesFiles = state.imagesFiles.filter(
        (file) => file.id !== action.payload.id
      );
    },

    // Fractional units add as list of units
    setFractionalOwnersUnitsToState: (
      state,
      action: PayloadAction<PropertyUnitModel[]>
    ) => {
      state.fractionalOwnersUnits = action.payload;
    },

    // Fractional owners units reducers
    addFractionalOwnerUnitToState: (
      state,
      action: PayloadAction<PropertyUnitModel>
    ) => {
      const index = state.fractionalOwnersUnits.findIndex(
        (unit) => unit.id === action.payload.id
      );

      // Case 1: If unit with the same number exists, update it (edit flow)
      if (index !== -1) {
        state.fractionalOwnersUnits[index] = action.payload;
      }
      // Case 2: If it's a new unit, add it to the list
      else {
        state.fractionalOwnersUnits.push(
          action.payload.copyWith({ id: Date.now() })
        ); // Assign a temporary ID (in real case, backend would provide this)
      }

      // Change the current step to the entries view after adding/editing a unit
      state.currentUnitDetailsStep = PropertyUnitView.manualEntry;
    },

    // Set property details to state (used in basic details step)
    setPropertyToUnitState: (state, action: PayloadAction<PropertyDetailsModel>) => {
      state.property = action.payload;
    },

    // Set property details to state (used in edit property flow)
    setEditPropertyToUnitState: (
      state,
      action: PayloadAction<PropertyDetailsModel>
    ) => {
      state.editProperty = action.payload;
      state.property = action.payload;
    },

    // Clear all state (used when navigating away from the create property flow)
    clearPropertyDataToUnitsState: (state) => {
      state.separateOwnersUnits = [];
      state.currentUnitDetailsStep = PropertyUnitView.manualEntry;
      state.currentTabStep = ManageCreatePropertySteps.BASIC_DETAILS;
      state.errors = [];
      state.legalDocsFiles = [];
      state.imagesFiles = [];
      state.fractionalOwnersUnits = [];
      state.property = new PropertyDetailsModel({});
    },
  },
});

export const {
  setPropertyUnitsToState,
  setPropertyUnitsFromExcelReportToState,
  setCurrentStepToUnitState,
  addPropertyUnitToState,
  setCurrentTabStepToUnitState,
  setErrorsToUnitState,
  removeUnitEntryFromState,
  setEditPropertyUnitToState,
  //
  setBulkLegalDocsFilesToUnitState,
  setLegalDocsFilesToUnitState,
  setImagesFilesToUnitState,
  removeLegalDocsFileFromUnitState,
  removeImageFileFromUnitState,
  //
  addFractionalOwnerUnitToState,
  setFractionalOwnersUnitsToState,
  //
  setPropertyToUnitState,
  setEditPropertyToUnitState,
  clearPropertyDataToUnitsState,
} = propertyUnitsSlice.actions;

export default propertyUnitsSlice.reducer;
